// GA4 (gtag.js) loader + helpers.
//
// Loads asynchronously when REACT_APP_GA_MEASUREMENT_ID is present. We keep
// the loader idempotent (safe to call multiple times) and shape every helper
// to be a no-op if GA4 is disabled — so callers never need to gate their
// usage. Track conversions by setting `is_conversion: true` (GA4's new way of
// marking events as key events / conversions, replacing the legacy
// `gtag('event','conversion')`).
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

const MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "";

const isPreviewHost = () =>
  typeof window !== "undefined" &&
  /(?:preview\.emergentagent\.com|localhost|127\.0\.0\.1)/.test(window.location.hostname);

// Conversion event names — keep these stable since they'll be referenced
// inside the GA4 admin UI when marking key events / building audiences.
export const EVENTS = {
  NEWSLETTER_SIGNUP: "newsletter_signup",
  INQUIRY_SUBMIT: "inquiry_submit",
  CONTACT_FORM_SUBMIT: "contact_form_submit",
};

let loaded = false;

export const initAnalytics = () => {
  if (loaded || !MEASUREMENT_ID || typeof window === "undefined") return;
  loaded = true;

  // Mark preview / dev traffic so it can be filtered in GA4 (Property Settings
  // → Data Filters → Internal Traffic). This lets you compare apples-to-apples
  // across environments without contaminating production analytics.
  const traffic_type = isPreviewHost() ? "internal" : "external";

  // Inject the gtag script.
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`;
  document.head.appendChild(s);

  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = gtag;
  gtag("js", new Date());
  // We disable automatic page_view firing so React Router route changes don't
  // double-count (gtag.js's default behavior assumes a server-rendered page
  // reload per navigation). We fire page_view manually from RouteTracker.
  gtag("config", MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
    traffic_type,
  });
};

export const trackPageView = (path) => {
  if (!MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const trackEvent = (name, params = {}) => {
  if (!MEASUREMENT_ID || typeof window === "undefined" || !window.gtag) return;
  window.gtag("event", name, params);
};

// Mark a conversion. GA4's new model uses `is_conversion: true` on the event;
// in GA4 admin you then mark the event name as a "Key event" to count it
// across the conversion reports.
export const trackConversion = (name, params = {}) => {
  trackEvent(name, { ...params, is_conversion: true });
};

// Hook: fires page_view on every React Router navigation.
export const RouteTracker = () => {
  const location = useLocation();
  const last = useRef("");

  useEffect(() => {
    // Initialize once on first mount.
    initAnalytics();
  }, []);

  useEffect(() => {
    const path = location.pathname + location.search;
    if (path === last.current) return;
    last.current = path;
    // Wait a tick so document.title has been updated by SEO.jsx before we
    // report the page_title to GA4 (otherwise every event reports the
    // previous page's title).
    const t = setTimeout(() => trackPageView(path), 80);
    return () => clearTimeout(t);
  }, [location.pathname, location.search]);

  return null;
};
